import * as consts from 'consts/consts';

import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button } from 'reactstrap';
import { Campaign, Me, Organization, Person, ReduxState } from 'types';
import { useProxyParameters } from 'utils/api/ApiHooks';
import { useIsCalibrationViewEditor } from 'utils/models/User';
import PageError from 'views/Layout/Pages/Errors/PageError';
import EmptyState from '../EmptyState';
import { AdvancedDatasetFilterContextProvider } from '../Filters/AdvancedSpecializedFilter';
import { ApiGroup } from '../Filters/AdvancedSpecializedFilter/types';
import Loading from '../Loading';
import { calibrationViewsApi } from './CalibrationDashboard/api/calibrationViewsApi';
import CalibrationView from './CalibrationDashboard/components/CalibrationView';
import CalibrationViewList from './CalibrationDashboard/components/CalibrationViewList';
import CalibrationViewModal from './CalibrationDashboard/components/CalibrationViewModal';
import { useCalibrationData } from './CalibrationDashboard/hooks/useCalibrationData';
import { useCalibrationViews } from './CalibrationDashboard/hooks/useCalibrationViews';
import { CalibrationViewSummary } from './CalibrationDashboard/types';

interface Props {
  campaign?: Campaign;
  campaigns?: Campaign[];
  me: Me;
  currentOrganization?: Organization;
  currentProxyPerson?: Person;
}

const dummy = () => {};

const CalibrationDashboard: React.FC<Props> = ({
  campaign,
  campaigns,
  me,
  currentOrganization,
  currentProxyPerson,
}) => {
  const { formatMessage } = useIntl();
  const isHRBP = currentOrganization?.is_hrbp ?? false;
  // Calibration view editors may create (and delete their own) views of their
  // cycles, but unlike HRBPs they can't load the HRBP-scoped dataset filters,
  // so isHRBP keeps gating those
  const isCalibrationViewEditor = useIsCalibrationViewEditor(campaign?.id);
  const canManageViews = isHRBP || isCalibrationViewEditor;
  const { viewId } = useParams<{ viewId?: string }>();
  const proxyParameters = useProxyParameters();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const selectedCampaignId = campaign?.id;
  const [calibrationViews, setCalibrationViews] = useState<
    CalibrationViewSummary[]
  >([]);
  const [adminFilter, setAdminFilter] = useState<ApiGroup | undefined>(
    undefined
  );

  //  Use useCalibrationData hook for data management
  const { isLoading, errorMessage } = useCalibrationData({
    selectedCampaignId: campaign?.id ?? null,
    calibrationViews,
    setCalibrationViews,
    setAdminFilter,
  });

  const hasViews = calibrationViews && calibrationViews.length > 0;

  const selectedView = useMemo(() => {
    if (viewId) {
      const id = parseInt(viewId);
      return calibrationViews.find((view) => view.id === id);
    }
    return undefined;
  }, [calibrationViews, viewId]);

  const calibrationBasePath = useMemo(
    () => consts.CALIBRATION_DASHBOARD(formatMessage).path,
    [formatMessage]
  );

  // Use useCalibrationViews hook for view management
  const { handleSelectView, handleBackToList } = useCalibrationViews({
    calibrationBasePath,
    calibrationViews,
  });

  const openCreateModal = useCallback(() => {
    setIsCreateModalOpen(true);
  }, [setIsCreateModalOpen]);

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, [setIsCreateModalOpen]);

  const handleViewSuccess = useCallback((views: CalibrationViewSummary[]) => {
    setCalibrationViews((prevViews) => {
      const updatedViews = [...prevViews];

      for (const view of views) {
        const existingIndex = updatedViews.findIndex((v) => v.id === view.id);
        if (existingIndex >= 0) {
          // Replace the existing view
          updatedViews[existingIndex] = view;
        } else {
          // Add the new view to the list
          updatedViews.push(view);
        }
      }

      return updatedViews;
    });
  }, []);

  const handleDeleteView = useCallback(
    (
      viewId: number,
      onSuccess: () => void,
      onError: (error: string) => void
    ) => {
      calibrationViewsApi.delete(
        String(viewId),
        proxyParameters,
        () => {
          setCalibrationViews((prevViews) =>
            prevViews.filter((v) => v.id !== viewId)
          );
          onSuccess();
        },
        onError
      );
    },
    [proxyParameters]
  );

  const viewModal = campaign &&
    campaign.id &&
    currentOrganization &&
    currentOrganization.id &&
    canManageViews && (
      <CalibrationViewModal
        me={me}
        isHRBP={isHRBP}
        currentProxyPerson={currentProxyPerson}
        isOpen={isCreateModalOpen}
        adminFilter={adminFilter}
        mode={'create'}
        campaign={campaign}
        organizationId={currentOrganization.id}
        onSuccess={handleViewSuccess}
        onClose={closeCreateModal}
      />
    );

  if (isLoading) {
    return <Loading />;
  }

  const hasCalibrationPhase = campaign?.phases?.some((p) => p.type === 'C');

  if (!hasCalibrationPhase && !!campaigns?.length) {
    return (
      <EmptyState
        wrapInCard={true}
        title={formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.empty_state.calibration_not_active',
          defaultMessage: 'Calibrations are not active in this cycle',
        })}
        subtitle={formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.empty_state.calibration_not_active.subtitle',
          defaultMessage: 'Please select another one from the cycle selector',
        })}
      />
    );
  }

  if (!selectedView) {
    if (!campaigns?.length) {
      return (
        <EmptyState
          wrapInCard={true}
          title={formatMessage({
            id: 'app.views.widgets.dashboards.calibration_dashboard.empty_state.no_campaigns',
            defaultMessage: 'No available campaigns.',
          })}
          subtitle={formatMessage({
            id: 'app.views.widgets.dashboards.calibration_dashboard.empty_state.no_campaign_selected',
            defaultMessage:
              'Choose a performance cycle to explore calibration insights.',
          })}
        />
      );
    }

    if (!selectedCampaignId) {
      return (
        <EmptyState
          wrapInCard={true}
          title={formatMessage({
            id: 'app.views.widgets.dashboards.calibration_dashboard.no_campaign_selected',
            defaultMessage: 'No campaign selected',
          })}
        ></EmptyState>
      );
    }

    if (!hasViews) {
      return (
        <EmptyState
          wrapInCard={true}
          title={formatMessage({
            id: 'app.views.widgets.dashboards.calibration_dashboard.empty_state.title',
            defaultMessage: 'No calibration views yet',
          })}
          subtitle={
            canManageViews
              ? formatMessage({
                  id: 'app.views.widgets.dashboards.calibration_dashboard.empty_state.subtitle',
                  defaultMessage:
                    'Create your first calibration view to gather early feedback from calibrators before performance review sessions.',
                })
              : formatMessage({
                  id: 'app.views.widgets.dashboards.calibration_dashboard.empty_state.subtitle_reviewer',
                  defaultMessage:
                    "Wait for your HRBPs to publish the calibration views for the session you'll be part of ",
                })
          }
        >
          {canManageViews && (
            <div className="d-flex gap-2 justify-content-center">
              <Button color="primary" onClick={openCreateModal}>
                <FormattedMessage
                  id="app.views.widgets.dashboards.calibration_dashboard.create_calibration_view_button"
                  defaultMessage="Create calibration view"
                />
              </Button>
            </div>
          )}
          {canManageViews && viewModal}
        </EmptyState>
      );
    }

    return (
      <>
        {isHRBP && (
          <AdvancedDatasetFilterContextProvider
            campaignId={campaign.id}
            roleScope={'HRBP'}
            includeIndividualsFilters={true}
          >
            <CalibrationViewList
              calibrationViews={calibrationViews}
              onSelectView={handleSelectView}
              onCreateView={canManageViews ? openCreateModal : undefined}
              isHRBP={isHRBP}
              currentPerson={me}
              onDeleteView={handleDeleteView}
              onViewsUpdated={handleViewSuccess}
            />
          </AdvancedDatasetFilterContextProvider>
        )}
        {!isHRBP && (
          <CalibrationViewList
            calibrationViews={calibrationViews}
            onSelectView={handleSelectView}
            onCreateView={canManageViews ? openCreateModal : undefined}
            isHRBP={isHRBP}
            currentPerson={me}
            onDeleteView={handleDeleteView}
            onViewsUpdated={handleViewSuccess}
          />
        )}
        {canManageViews && viewModal}
      </>
    );
  }

  if (errorMessage) {
    return <PageError message={errorMessage} />;
  }

  return (
    <CalibrationView
      campaign={campaign}
      campaigns={campaigns}
      me={me}
      currentOrganization={currentOrganization}
      currentProxyPerson={currentProxyPerson}
      selectedView={selectedView}
      setPageStatusIndicator={dummy}
      handleBackToList={handleBackToList}
      handleUpdate={handleViewSuccess}
    />
  );
};

const mapStateToProps = (state: ReduxState) => {
  const { currentOrganization, currentProxyPerson, me } = state;
  return {
    currentOrganization,
    currentProxyPerson,
    me,
  };
};

export default connect(mapStateToProps)(React.memo(CalibrationDashboard));
